import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, Mail, MailOpen, CheckCircle, CalendarClock, 
  FileText, Plus, Trash2, Clock, ChevronDown, ChevronUp, Loader2, Reply
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CustomerCorrespondence({ customerId, apiClient }) {
  const [correspondence, setCorrespondence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (customerId) {
      fetchCorrespondence();
    }
  }, [customerId]);

  const fetchCorrespondence = async () => {
    try {
      const res = await apiClient.get(`/customers/${customerId}/correspondence`);
      setCorrespondence(res.data);
    } catch (err) {
      console.error('Failed to load correspondence:', err);
    }
    setLoading(false);
  };

  const addNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/customers/${customerId}/correspondence`, {
        customer_id: customerId,
        type: 'manual_note',
        subject: 'Note',
        content: newNote,
        direction: 'internal'
      });
      toast.success('Note added');
      setNewNote('');
      setShowAddNote(false);
      fetchCorrespondence();
    } catch (err) {
      toast.error('Failed to add note');
    }
    setSaving(false);
  };

  const deleteNote = async (corrId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await apiClient.delete(`/customers/${customerId}/correspondence/${corrId}`);
      toast.success('Note deleted');
      fetchCorrespondence();
    } catch (err) {
      toast.error('Failed to delete note');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'email_sent':
        return <Mail className="w-4 h-4 text-blue-400" />;
      case 'email_reply':
        return <Reply className="w-4 h-4 text-emerald-400" />;
      case 'confirmation':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'reschedule_request':
        return <CalendarClock className="w-4 h-4 text-yellow-400" />;
      case 'manual_note':
        return <FileText className="w-4 h-4 text-purple-400" />;
      default:
        return <MessageSquare className="w-4 h-4 text-zinc-400" />;
    }
  };

  const getTypeBadge = (type, direction) => {
    const badges = {
      email_sent: { label: 'Email Sent', className: 'bg-blue-500/20 text-blue-400' },
      email_reply: { label: 'Customer Reply', className: 'bg-emerald-500/20 text-emerald-400' },
      confirmation: { label: 'Confirmed', className: 'bg-green-500/20 text-green-400' },
      reschedule_request: { label: 'Reschedule Request', className: 'bg-yellow-500/20 text-yellow-400' },
      manual_note: { label: 'Note', className: 'bg-purple-500/20 text-purple-400' }
    };
    const badge = badges[type] || { label: type, className: 'bg-zinc-500/20 text-zinc-400' };
    return <Badge className={badge.className}>{badge.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Correspondence & History
            <Badge variant="outline" className="ml-2 text-zinc-400 border-zinc-600">
              {correspondence.length}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
              onClick={(e) => { e.stopPropagation(); setShowAddNote(true); }}
            >
              <Plus className="w-4 h-4 mr-1" /> Add Note
            </Button>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-zinc-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-zinc-400" />
            )}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="pt-0">
          {correspondence.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No correspondence history yet</p>
              <p className="text-sm mt-1">Emails, confirmations, and notes will appear here</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {correspondence.map((item) => (
                <div 
                  key={item.id} 
                  className={`bg-zinc-800/50 rounded-lg p-4 border-l-4 ${
                    item.type === 'confirmation' ? 'border-l-green-500' :
                    item.type === 'reschedule_request' ? 'border-l-yellow-500' :
                    item.type === 'email_reply' ? 'border-l-emerald-500' :
                    item.type === 'manual_note' ? 'border-l-purple-500' :
                    'border-l-blue-500'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">{getTypeIcon(item.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {getTypeBadge(item.type, item.direction)}
                          {item.direction === 'inbound' && (
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-xs">
                              From Customer
                            </Badge>
                          )}
                          {item.job_id && item.metadata?.job_title && (
                            <span className="text-xs text-zinc-500">
                              Re: {item.metadata.job_title}
                            </span>
                          )}
                        </div>
                        {item.subject && item.type !== 'manual_note' && (
                          <p className="text-white font-medium text-sm">{item.subject}</p>
                        )}
                        <p className="text-zinc-300 text-sm mt-1 whitespace-pre-wrap">{item.content}</p>
                        
                        {/* Show metadata for confirmations/reschedules */}
                        {item.type === 'reschedule_request' && item.metadata && (
                          <div className="mt-2 bg-zinc-900/50 rounded p-2 text-xs">
                            {item.metadata.preferred_reschedule_date && (
                              <p className="text-zinc-400">
                                Preferred Date: <span className="text-white">{item.metadata.preferred_reschedule_date}</span>
                              </p>
                            )}
                            {item.metadata.preferred_reschedule_time && (
                              <p className="text-zinc-400">
                                Preferred Time: <span className="text-white">{item.metadata.preferred_reschedule_time}</span>
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(item.created_at)}
                          </span>
                          {item.created_by_name && (
                            <span>by {item.created_by_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {item.type === 'manual_note' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-zinc-500 hover:text-red-400"
                        onClick={() => deleteNote(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* Add Note Dialog */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note about this customer..."
              className="bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNote(false)} className="border-zinc-600">
              Cancel
            </Button>
            <Button onClick={addNote} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default CustomerCorrespondence;
